// Store references to our event handlers so we can remove them.
/** @type {{ keydown?: (e: KeyboardEvent) => void; focusin?: (e: FocusEvent) => void; focusout?: (e: FocusEvent) => void }} */
const trapFocusHandlers = {};

/** @type {HTMLElement | null} */
let trapFocusContainer = null;

/**
 * @returns {HTMLElement | null}
 */
export function getActiveElementDeep() {
  let active = document.activeElement;
  while (active instanceof Element && active.shadowRoot?.activeElement) {
    active = active.shadowRoot.activeElement;
  }
  return active instanceof HTMLElement ? active : null;
}

/**
 * Walk ancestors (crossing shadow root boundaries) to see if `node` is inside `container`.
 * @param {HTMLElement} container
 * @param {Node | null} node
 * @returns {boolean}
 */
function isNodeWithinContainer(container, node) {
  if (!node) return false;
  /** @type {Node | null} */
  let n = node;
  while (n) {
    if (n === container) return true;
    /** @type {ParentNode | null} */
    const p = n.parentNode;
    if (p instanceof ShadowRoot) {
      n = p.host;
    } else {
      n = p instanceof Node ? p : null;
    }
  }
  return false;
}

/**
 * Index of the focusable that owns `active` (including shadow descendants).
 * @param {HTMLElement[]} focusable
 * @param {HTMLElement | null} active
 * @returns {number}
 */
function focusableIndexForActive(focusable, active) {
  if (!active) return -1;
  /** @type {Node | null} */
  let node = active;
  while (node) {
    if (node instanceof HTMLElement) {
      const idx = focusable.indexOf(node);
      if (idx !== -1) return idx;
    }
    /** @type {ParentNode | null} */
    const p = node.parentNode;
    if (p instanceof ShadowRoot) {
      node = p.host;
    } else {
      node = p instanceof Node ? p : null;
    }
  }
  return -1;
}

const FOCUSABLE_SELECTOR =
  "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe";

/**
 * @param {HTMLElement} el
 * @returns {boolean}
 */
function isFocusableCandidate(el) {
  return (
    el instanceof HTMLElement &&
    !el.hasAttribute('hidden') &&
    !el.closest('[hidden]') &&
    !el.closest('[inert]')
  );
}

/**
 * Elements inside `display:none` (etc.) are still matched by `querySelectorAll`, but the browser
 * skips them in the tab order — treating them as "last" breaks wrapping and lets Tab escape to
 * the browser UI (e.g. empty predictive search with a hidden "View all" footer button).
 * @param {HTMLElement} el
 * @returns {boolean}
 */
function isTabbableInLayout(el) {
  if (!isFocusableCandidate(el)) return false;
  try {
    if (typeof el.checkVisibility === 'function') {
      return el.checkVisibility({
        checkOpacity: true,
        checkVisibilityCSS: true,
      });
    }
  } catch {
    // ignore
  }
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

/**
 * Collect focusables under root (including open shadow roots). `querySelector` on the document
 * does not pierce shadow trees, so descendants like `predictive-search-component` would otherwise
 * yield an empty list until content is incorrectly assumed missing.
 * @param {Element | ShadowRoot} root
 * @param {HTMLElement[]} acc
 */
function collectFocusableElements(root, acc) {
  root.querySelectorAll(FOCUSABLE_SELECTOR).forEach((el) => {
    if (el instanceof HTMLElement && isTabbableInLayout(el)) {
      acc.push(el);
    }
  });

  root.querySelectorAll('*').forEach((node) => {
    if (node instanceof HTMLElement && node.shadowRoot) {
      collectFocusableElements(node.shadowRoot, acc);
    }
  });
}

/**
 * Get all focusable elements within a container.
 * @param {HTMLElement} container - The container to get focusable elements from.
 * @returns {HTMLElement[]} An array of focusable elements.
 */
export function getFocusableElements(container) {
  /** @type {HTMLElement[]} */
  const acc = [];
  collectFocusableElements(container, acc);
  return Array.from(new Set(acc));
}

/**
 * Trap focus within the given container.
 * @param {HTMLElement} container - The container to trap focus within.
 * @param {{ focusContainer?: boolean }} [options] - When `focusContainer` is false, focus moves to the first focusable child instead of the container (better for native `<dialog>`).
 */
export function trapFocus(container, options = {}) {
  const { focusContainer = true } = options;

  // Clean up any previously set traps.
  removeTrapFocus();

  trapFocusContainer = container;

  const focusablesNow = () => getFocusableElements(container);
  const initial = focusablesNow();

  // Keydown handler for cycling focus with Tab and Shift+Tab (re-query each time so dynamic content stays correct)
  /** @type {(event: KeyboardEvent) => void} */
  trapFocusHandlers.keydown = (event) => {
    if (event.key !== 'Tab') return;

    const focusable = focusablesNow();
    if (!focusable.length) return;

    const active = getActiveElementDeep();
    if (!active || !isNodeWithinContainer(container, active)) return;

    const idx = focusableIndexForActive(focusable, active);
    if (idx === -1) return;

    if (!event.shiftKey && idx === focusable.length - 1) {
      event.preventDefault();
      event.stopPropagation();
      focusable[0]?.focus();
    } else if (event.shiftKey && idx === 0) {
      event.preventDefault();
      event.stopPropagation();
      focusable[focusable.length - 1]?.focus();
    }
  };

  // Focusin (capturing) handler to forcibly keep focus in the container
  /** @type {(event: FocusEvent) => void} */
  trapFocusHandlers.focusin = (event) => {
    const target = event.target instanceof Node ? event.target : null;
    if (!target || isNodeWithinContainer(container, target)) return;

    event.stopPropagation();
    focusablesNow()[0]?.focus();
  };

  /** @type {(event: FocusEvent) => void} */
  trapFocusHandlers.focusout = (event) => {
    if (!(event.target instanceof Node) || !isNodeWithinContainer(container, event.target)) return;
    const related = event.relatedTarget;
    if (related instanceof Node && isNodeWithinContainer(container, related)) return;

    queueMicrotask(() => {
      const refocusIfOutside = () => {
        const cur = getActiveElementDeep();
        if (!cur || !isNodeWithinContainer(container, cur)) {
          const list = focusablesNow();
          list[0]?.focus();
        }
      };
      refocusIfOutside();
      requestAnimationFrame(refocusIfOutside);
    });
  };

  // Attach the handlers
  document.addEventListener('keydown', trapFocusHandlers.keydown, true);
  document.addEventListener('focusin', trapFocusHandlers.focusin, true);
  container.addEventListener('focusout', trapFocusHandlers.focusout, true);

  if (focusContainer && typeof container.focus === 'function') {
    container.focus();
  } else if (initial.length) {
    initial[0]?.focus();
  }
}

/**
 * Remove focus trap and optionally refocus another element.
 */
export function removeTrapFocus() {
  trapFocusHandlers.keydown && document.removeEventListener('keydown', trapFocusHandlers.keydown, true);
  trapFocusHandlers.focusin && document.removeEventListener('focusin', trapFocusHandlers.focusin, true);
  trapFocusHandlers.focusout && trapFocusContainer?.removeEventListener('focusout', trapFocusHandlers.focusout, true);
  delete trapFocusHandlers.keydown;
  delete trapFocusHandlers.focusin;
  delete trapFocusHandlers.focusout;
  trapFocusContainer = null;
}

/**
 * Cycle focus to the next or previous link
 *
 * @param {HTMLElement[]} items
 * @param {number} increment
 */
export function cycleFocus(items, increment) {
  const currentIndex = items.findIndex((item) => item.matches(':focus'));
  let targetIndex = currentIndex + increment;

  if (targetIndex >= items.length) {
    targetIndex = 0;
  } else if (targetIndex < 0) {
    targetIndex = items.length - 1;
  }

  const targetItem = items[targetIndex];

  if (!targetItem) return;

  targetItem.focus();
}
